import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Icon from '@/components/ui/Icon';
import N8NWebhookService from '@/services/n8n/N8NWebhookService';

/**
 * N8N Workflow Tester Component
 * Provides comprehensive testing tools for the N8N webhook workflow
 */
const N8NWorkflowTester = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [configStatus, setConfigStatus] = useState(null);

  // Test connection status
  const testConnection = async () => {
    setIsLoading(true);
    try {
      console.log('🔍 Testing N8N webhook connection...');
      const isConnected = await N8NWebhookService.testConnection();
      setConnectionStatus(isConnected ? 'connected' : 'failed');
      
      const newResult = {
        id: Date.now(),
        type: 'connection',
        title: 'Test Mode Connection Test',
        status: isConnected ? 'success' : 'error',
        message: isConnected ? 'Test mode webhook connection successful' : 'Test mode webhook connection failed',
        details: {
          mode: 'test',
          bypassGmailApi: true
        },
        timestamp: new Date().toLocaleTimeString()
      };
      
      setTestResults(prev => [newResult, ...prev]);
    } catch (error) {
      console.error('Connection test error:', error);
      setConnectionStatus('error');
      setTestResults(prev => [{
        id: Date.now(),
        type: 'connection',
        title: 'Test Mode Connection Test',
        status: 'error',
        message: `Test mode connection failed: ${error.message}`,
        details: {
          mode: 'test',
          error: error.message
        },
        timestamp: new Date().toLocaleTimeString()
      }, ...prev]);
    } finally {
      setIsLoading(false);
    }
  };

  // Test production connection with real user data
  const testProductionConnection = async () => {
    setIsLoading(true);
    try {
      console.log('🚀 Testing production webhook connection...');
      
      // Get current user ID (you might need to adjust this based on your auth system)
      const currentUserId = 'current-user-id'; // Replace with actual user ID
      
      const isConnected = await N8NWebhookService.testProductionConnection(currentUserId);
      setConnectionStatus(isConnected ? 'connected' : 'failed');
      
      const newResult = {
        id: Date.now(),
        type: 'production-connection',
        title: 'Production Mode Connection Test',
        status: isConnected ? 'success' : 'error',
        message: isConnected ? 'Production mode webhook connection successful' : 'Production mode webhook connection failed',
        details: {
          mode: 'production',
          userId: currentUserId,
          bypassGmailApi: false
        },
        timestamp: new Date().toLocaleTimeString()
      };
      
      setTestResults(prev => [newResult, ...prev]);
    } catch (error) {
      console.error('Production connection test error:', error);
      setConnectionStatus('error');
      setTestResults(prev => [{
        id: Date.now(),
        type: 'production-connection',
        title: 'Production Mode Connection Test',
        status: 'error',
        message: `Production mode connection failed: ${error.message}`,
        details: {
          mode: 'production',
          error: error.message
        },
        timestamp: new Date().toLocaleTimeString()
      }, ...prev]);
    } finally {
      setIsLoading(false);
    }
  };

  // Test with realistic Gmail data
  const testRealisticWorkflow = async (campType = 'summer-camp') => {
    setIsLoading(true);
    try {
      console.log(`🧪 Testing realistic ${campType} workflow...`);
      const result = await N8NWebhookService.testWorkflowWithRealisticData({ campType });
      
      const newResult = {
        id: Date.now(),
        type: 'workflow',
        title: `Realistic ${campType.replace('-', ' ')} Test`,
        status: result.success ? 'success' : 'error',
        message: result.message,
        details: result.success ? {
          messageId: result.result?.messageId,
          processingTime: result.result?.processingTime
        } : {
          error: result.error
        },
        timestamp: new Date().toLocaleTimeString()
      };
      
      setTestResults(prev => [newResult, ...prev]);
    } catch (error) {
      console.error('Realistic workflow test error:', error);
      setTestResults(prev => [{
        id: Date.now(),
        type: 'workflow',
        title: `Realistic ${campType.replace('-', ' ')} Test`,
        status: 'error',
        message: `Test failed: ${error.message}`,
        timestamp: new Date().toLocaleTimeString()
      }, ...prev]);
    } finally {
      setIsLoading(false);
    }
  };

  // Test multiple camp types
  const testMultipleCampTypes = async () => {
    setIsLoading(true);
    try {
      console.log('🧪 Testing multiple camp types...');
      const results = await N8NWebhookService.testMultipleCampTypes();
      
      results.forEach(result => {
        const newResult = {
          id: Date.now() + Math.random(),
          type: 'multi-test',
          title: `Multi-Test: ${result.campType.replace('-', ' ')}`,
          status: result.success ? 'success' : 'error',
          message: result.message,
          details: result.error ? { error: result.error } : null,
          timestamp: new Date().toLocaleTimeString()
        };
        setTestResults(prev => [newResult, ...prev]);
      });
    } catch (error) {
      console.error('Multiple camp type test error:', error);
      setTestResults(prev => [{
        id: Date.now(),
        type: 'multi-test',
        title: 'Multiple Camp Types Test',
        status: 'error',
        message: `Multi-test failed: ${error.message}`,
        timestamp: new Date().toLocaleTimeString()
      }, ...prev]);
    } finally {
      setIsLoading(false);
    }
  };

  // Test error handling
  const testErrorHandling = async () => {
    setIsLoading(true);
    try {
      console.log('🧪 Testing error handling...');
      const results = await N8NWebhookService.testErrorHandling();
      
      results.forEach(result => {
        const newResult = {
          id: Date.now() + Math.random(),
          type: 'error-handling',
          title: `Error Test: ${result.testName}`,
          status: result.success ? 'success' : 'error',
          message: result.message,
          details: {
            expectedError: result.expectedError,
            actualError: result.error
          },
          timestamp: new Date().toLocaleTimeString()
        };
        setTestResults(prev => [newResult, ...prev]);
      });
    } catch (error) {
      console.error('Error handling test error:', error);
      setTestResults(prev => [{
        id: Date.now(),
        type: 'error-handling',
        title: 'Error Handling Test',
        status: 'error',
        message: `Error handling test failed: ${error.message}`,
        timestamp: new Date().toLocaleTimeString()
      }, ...prev]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get configuration status
  const getConfigStatus = () => {
    const config = N8NWebhookService.getConfigStatus();
    setConfigStatus(config);
    
    const newResult = {
      id: Date.now(),
      type: 'config',
      title: 'Configuration Status',
      status: 'info',
      message: 'Configuration status retrieved',
      details: config,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setTestResults(prev => [newResult, ...prev]);
  };

  // Clear test results
  const clearResults = () => {
    setTestResults([]);
    setConnectionStatus(null);
    setConfigStatus(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return 'check-circle';
      case 'error': return 'x-circle';
      case 'info': return 'info';
      default: return 'help-circle';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">N8N Workflow Tester</h1>
          <p className="text-gray-600 mt-2">
            Test your N8N webhook workflow with realistic Gmail data and comprehensive error handling
          </p>
        </div>
        <Button
          onClick={clearResults}
          variant="outline"
          disabled={testResults.length === 0}
        >
          <Icon name="trash-2" size="sm" className="mr-2" />
          Clear Results
        </Button>
      </div>

      {/* Configuration Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Icon name="settings" size="sm" className="mr-2" />
            Configuration Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Webhook URL</div>
              <Badge variant={configStatus?.webhookUrl ? 'default' : 'destructive'}>
                {configStatus?.webhookUrl ? 'Configured' : 'Missing'}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Webhook Secret</div>
              <Badge variant={configStatus?.webhookSecret ? 'default' : 'destructive'}>
                {configStatus?.webhookSecret ? 'Configured' : 'Missing'}
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Rate Limit Delay</div>
              <Badge variant="outline">{configStatus?.rateLimitDelay || 1000}ms</Badge>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">Batch Delay</div>
              <Badge variant="outline">{configStatus?.batchDelay || 2000}ms</Badge>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={getConfigStatus} variant="outline" size="sm">
              <Icon name="refresh-cw" size="sm" className="mr-2" />
              Refresh Config
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Icon name="play-circle" size="sm" className="mr-2" />
            Test Controls
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Connection Tests */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Connection Tests</h3>
              <div className="space-y-2">
                <Button
                  onClick={testConnection}
                  disabled={isLoading}
                  className="w-full"
                  variant="outline"
                >
                  {isLoading ? (
                    <>
                      <Icon name="loader-2" size="sm" className="mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Icon name="wifi" size="sm" className="mr-2" />
                      Test Mode Connection
                    </>
                  )}
                </Button>
                <Button
                  onClick={testProductionConnection}
                  disabled={isLoading}
                  className="w-full"
                  variant="secondary"
                >
                  {isLoading ? (
                    <>
                      <Icon name="loader-2" size="sm" className="mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Icon name="server" size="sm" className="mr-2" />
                      Production Mode Connection
                    </>
                  )}
                </Button>
              </div>
              {connectionStatus && (
                <Badge className={getStatusColor(connectionStatus)}>
                  <Icon name={getStatusIcon(connectionStatus)} size="sm" className="mr-1" />
                  {connectionStatus}
                </Badge>
              )}
            </div>

            {/* Realistic Workflow Tests */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Realistic Gmail Tests</h3>
              <div className="space-y-2">
                <Button
                  onClick={() => testRealisticWorkflow('summer-camp')}
                  disabled={isLoading}
                  size="sm"
                  className="w-full"
                >
                  <Icon name="sun" size="sm" className="mr-2" />
                  Summer Camp
                </Button>
                <Button
                  onClick={() => testRealisticWorkflow('sports-camp')}
                  disabled={isLoading}
                  size="sm"
                  className="w-full"
                  variant="outline"
                >
                  <Icon name="football" size="sm" className="mr-2" />
                  Sports Camp
                </Button>
                <Button
                  onClick={() => testRealisticWorkflow('art-camp')}
                  disabled={isLoading}
                  size="sm"
                  className="w-full"
                  variant="outline"
                >
                  <Icon name="palette" size="sm" className="mr-2" />
                  Art Camp
                </Button>
              </div>
            </div>

            {/* Advanced Tests */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Advanced Tests</h3>
              <div className="space-y-2">
                <Button
                  onClick={testMultipleCampTypes}
                  disabled={isLoading}
                  size="sm"
                  className="w-full"
                  variant="secondary"
                >
                  <Icon name="layers" size="sm" className="mr-2" />
                  Multiple Types
                </Button>
                <Button
                  onClick={testErrorHandling}
                  disabled={isLoading}
                  size="sm"
                  className="w-full"
                  variant="destructive"
                >
                  <Icon name="alert-triangle" size="sm" className="mr-2" />
                  Error Handling
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="list-checks" size="sm" className="mr-2" />
                Test Results ({testResults.length})
              </div>
              <Badge variant="outline">
                {testResults.filter(r => r.status === 'success').length} passed
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {testResults.map((result) => (
                <div
                  key={result.id}
                  className="border rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge className={getStatusColor(result.status)}>
                          <Icon name={getStatusIcon(result.status)} size="sm" className="mr-1" />
                          {result.status}
                        </Badge>
                        <Badge variant="outline">{result.type}</Badge>
                        <span className="text-sm text-gray-500">{result.timestamp}</span>
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">{result.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">{result.message}</p>
                      
                      {result.details && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                            View Details
                          </summary>
                          <pre className="mt-2 p-2 bg-white rounded border text-xs overflow-x-auto">
                            {JSON.stringify(result.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Icon name="book-open" size="sm" className="mr-2" />
            Testing Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">1. Basic Connection Test</h4>
              <p>Tests basic webhook connectivity with minimal test data. Should pass if your N8N webhook URL is correctly configured.</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">2. Realistic Gmail Tests</h4>
              <p>Tests the workflow with realistic camp-related email data that mimics actual Gmail messages. Each test uses different camp types (summer, sports, art) to verify the workflow can handle various scenarios.</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">3. Multiple Types Test</h4>
              <p>Runs all camp type tests in sequence with proper delays to respect rate limits. Good for comprehensive workflow validation.</p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-900 mb-2">4. Error Handling Test</h4>
              <p>Tests the workflow's ability to handle invalid data gracefully. Verifies that proper error messages are returned for malformed requests.</p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <h4 className="font-medium text-yellow-800 mb-1">⚠️ Important Notes</h4>
              <ul className="text-yellow-700 space-y-1">
                <li>• Make sure your N8N webhook URL is configured in environment variables</li>
                <li>• The workflow should be deployed and active in N8N</li>
                <li>• Tests include rate limiting delays to prevent API overload</li>
                <li>• Check console logs for detailed debugging information</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default N8NWorkflowTester;
